import React, { useMemo } from "react";
import ReactSelect, { components } from "react-select";
import ExecutiveSummaryReviewGateSelectOption from "./ExecutiveSummaryReviewGateSelectOption";
import {
    getAllowedExecutiveSummaryQaOptions,
    getExecutiveSummaryBandQaStatus,
    getExecutiveSummaryQaStatusLabel,
    EXEC_SUM_QA_OPTION_COLORS,
    REVIEW_GATE_STATUS,
    REVIEW_GATE_STATUS_CLASS
} from "./executiveSummaryQaStatus";

const qaStatusSelectMenuPortal = typeof document !== "undefined" && document.body ? document.body : null;

function ExecSumBandQaStatusMenu(props) {
    return <components.Menu {...props} className={`exec-sum-band-qa-status__menu ${props.className || ""}`.trim()} />;
}

const qaStatusSelectStyles = {
    menuPortal: base => ({ ...base, zIndex: 10050 }),
    menu: base => ({
        ...base,
        zIndex: 10050,
        fontSize: 12,
        borderRadius: 6,
        minWidth: 140
    }),
    option: (base, state) => ({
        ...base,
        fontSize: 12,
        padding: "7px 10px",
        cursor: state.isDisabled ? "not-allowed" : "pointer",
        backgroundColor: state.isSelected ? "#edf2f7" : state.isFocused ? "#f8fafc" : base.backgroundColor
    }),
    singleValue: (base, { data }) => ({
        ...base,
        fontSize: 12,
        fontWeight: 600,
        color: EXEC_SUM_QA_OPTION_COLORS[data?.value] || base.color
    }),
    control: base => ({
        ...base,
        fontSize: 12,
        minHeight: 28,
        minWidth: 130
    })
};

function ExecutiveSummaryBandQaStatus({
    band,
    canEditBandQaStatus = false,
    canMarkBandQaPassed = false,
    saving = false,
    onStatusChange
}) {
    const currentStatus = getExecutiveSummaryBandQaStatus(band);
    const options = useMemo(
        () => getAllowedExecutiveSummaryQaOptions({ canEdit: canEditBandQaStatus, canMarkQaPassed: canMarkBandQaPassed }),
        [canEditBandQaStatus, canMarkBandQaPassed]
    );

    const showReadOnlyPassed =
        canEditBandQaStatus && !canMarkBandQaPassed && currentStatus === REVIEW_GATE_STATUS.QA_PASSED;

    const readOnly = !canEditBandQaStatus || showReadOnlyPassed;

    const statusLabel = getExecutiveSummaryQaStatusLabel(currentStatus);
    const statusClass = REVIEW_GATE_STATUS_CLASS[currentStatus] || "";

    const handleChange = selected => {
        const nextStatus = selected?.value;
        if (!nextStatus || nextStatus === currentStatus || !onStatusChange) return;
        onStatusChange(nextStatus);
    };

    if (readOnly) {
        return (
            <div
                className={`exec-sum-band-qa-status exec-sum-band-qa-status--readonly ${statusClass}`}
                aria-label={`QA status: ${statusLabel}`}
            >
                {showReadOnlyPassed ? (
                    <span className="exec-sum-band-qa-status__passed-badge">QA/QC Passed</span>
                ) : (
                    <span className="exec-sum-band-qa-status__label">{statusLabel}</span>
                )}
            </div>
        );
    }

    return (
        <div
            className="exec-sum-band-qa-status exec-sum-band-qa-status--editable"
            onClick={e => e.stopPropagation()}
            onKeyDown={e => e.stopPropagation()}
        >
            <ReactSelect
                className={`react-select-container exec-sum-band-qa-status__select ${statusClass}`}
                classNamePrefix="react-select"
                value={options.find(option => option.value === currentStatus)}
                onChange={handleChange}
                options={options}
                isSearchable={false}
                isDisabled={saving}
                menuPortalTarget={qaStatusSelectMenuPortal}
                styles={qaStatusSelectStyles}
                menuPlacement="auto"
                components={{ Menu: ExecSumBandQaStatusMenu, Option: ExecutiveSummaryReviewGateSelectOption }}
                aria-label="Band QA status"
            />
        </div>
    );
}

export default ExecutiveSummaryBandQaStatus;
